/**
 * The order round itself: name, status, closing date and time (South African
 * time), the words at the top of the staff page and the size guide link —
 * saved together — plus "Open ordering" / "Close ordering".
 */
import { useEffect, useId, useMemo, useState } from 'react'
import { Loader2, Lock, Save, Unlock } from 'lucide-react'
import toast from 'react-hot-toast'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useConfirm } from '@/hooks/useConfirm'
import {
  errorDetail,
  formatSast,
  formatSastShort,
  fromSastParts,
  isFuture,
  safeLink,
  toSastParts,
} from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderRoundDetail,
  type StaffOrderRoundUpdate,
} from '@/services/api/staffOrderService'

import { STATUS_BADGE } from './helpers'

interface FormState {
  name: string
  date: string
  time: string
  intro: string
  guide: string
}

interface RoundSettingsProps {
  detail: StaffOrderRoundDetail
  onUpdated: (detail: StaffOrderRoundDetail) => void
}

export function RoundSettings({ detail, onUpdated }: RoundSettingsProps) {
  const { confirm } = useConfirm()
  const { id, name, closes_at, intro_text, size_guide_url } = detail
  const initial = useMemo<FormState>(() => {
    const parts = toSastParts(closes_at)
    return {
      name,
      date: parts?.date ?? '',
      time: parts?.time ?? '23:59',
      intro: intro_text ?? '',
      guide: size_guide_url ?? '',
    }
  }, [name, closes_at, intro_text, size_guide_url])
  const [form, setForm] = useState<FormState>(initial)
  const [saving, setSaving] = useState(false)
  const [changingStatus, setChangingStatus] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fieldIds = { name: useId(), date: useId(), time: useId(), intro: useId(), guide: useId() }

  // A saved change (or another round) comes back from the server: start from it.
  useEffect(() => {
    setForm(initial)
    setError(null)
  }, [initial, id])

  const dirty =
    form.name !== initial.name ||
    form.intro !== initial.intro ||
    form.guide !== initial.guide ||
    form.date !== initial.date ||
    (form.date !== '' && form.time !== initial.time)

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    setError(null)
    const body: StaffOrderRoundUpdate = {}
    if (form.name !== initial.name) {
      if (!form.name.trim()) {
        setError('The order round needs a name.')
        return
      }
      body.name = form.name.trim()
    }
    if (form.date !== initial.date || (form.date && form.time !== initial.time)) {
      if (!form.date) {
        body.closes_at = null
      } else {
        const iso = fromSastParts(form.date, form.time)
        if (!iso) {
          setError('Enter a valid closing date and time.')
          return
        }
        body.closes_at = iso
      }
    }
    if (form.intro !== initial.intro) body.intro_text = form.intro.trim() || null
    if (form.guide !== initial.guide) {
      const guide = form.guide.trim()
      if (guide && !safeLink(guide)) {
        setError('Enter the size guide as a full web address, starting with https://')
        return
      }
      body.size_guide_url = guide || null
    }
    if (Object.keys(body).length === 0) return
    setSaving(true)
    try {
      const next = await staffOrderService.updateRound(id, body)
      onUpdated(next)
      toast.success('Order round saved.')
    } catch (err) {
      setError(errorDetail(err, 'The changes could not be saved. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async (status: 'open' | 'closed') => {
    const closes = formatSast(closes_at)
    const ok =
      status === 'open'
        ? await confirm(
            `Everyone with a voucher will be able to order until ${closes}.\n\nNobody is emailed yet: use "Send invitations" when you are ready.`,
            'Open ordering?',
            { confirmText: 'Open ordering', type: 'info' },
          )
        : await confirm(
            'Nobody will be able to place, change or cancel an order after this. You can open ordering again later.',
            'Close ordering?',
            { confirmText: 'Close ordering', type: 'warning' },
          )
    if (!ok) return
    setChangingStatus(true)
    setError(null)
    try {
      const next = await staffOrderService.updateRound(id, { status })
      onUpdated(next)
      toast.success(status === 'open' ? 'Ordering is open.' : 'Ordering is closed.')
    } catch (err) {
      setError(errorDetail(err, 'The order round could not be changed. Please try again.'))
    } finally {
      setChangingStatus(false)
    }
  }

  const status = STATUS_BADGE[detail.status]
  const closingPassed = !!closes_at && !isFuture(closes_at)
  let openBlocked: string | null = null
  if (dirty) openBlocked = 'Save your changes first.'
  else if (!closes_at) openBlocked = 'Set a closing date first.'
  else if (closingPassed) openBlocked = 'The closing date has passed. Set a later one first.'
  else if (!detail.items.some((i) => i.is_active)) openBlocked = 'Switch on at least one item first.'

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{detail.name}</h3>
              <Badge className={status.className}>{status.label}</Badge>
            </div>
            <p className="text-xs text-gray-500">
              {closes_at ? `Closes ${formatSast(closes_at)}` : 'No closing date yet'}
              {detail.opened_at && ` · Opened ${formatSastShort(detail.opened_at)}`}
              {detail.closed_at && ` · Closed ${formatSastShort(detail.closed_at)}`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {detail.status === 'open' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => changeStatus('closed')}
                disabled={changingStatus || dirty}
              >
                {changingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Close ordering
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={() => changeStatus('open')}
                disabled={changingStatus || !!openBlocked}
              >
                {changingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                Open ordering
              </Button>
            )}
            {detail.status !== 'open' && openBlocked && (
              <p className="text-xs text-gray-500">{openBlocked}</p>
            )}
            {detail.status === 'open' && dirty && (
              <p className="text-xs text-gray-500">Save your changes first.</p>
            )}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor={fieldIds.name}>Name</Label>
            <Input id={fieldIds.name} value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={200} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={fieldIds.date}>Closing date</Label>
            <Input id={fieldIds.date} type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={fieldIds.time}>Closing time (South African time)</Label>
            <Input id={fieldIds.time} type="time" value={form.time} onChange={(e) => set({ time: e.target.value })} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor={fieldIds.intro}>Words at the top of the order page</Label>
            <Textarea
              id={fieldIds.intro}
              rows={5}
              value={form.intro}
              onChange={(e) => set({ intro: e.target.value })}
              placeholder="What staff read before they choose. Line breaks are kept."
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor={fieldIds.guide}>Size guide link (optional)</Label>
            <Input
              id={fieldIds.guide}
              value={form.guide}
              onChange={(e) => set({ guide: e.target.value })}
              placeholder="https://…"
              inputMode="url"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {dirty && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setForm(initial)} disabled={saving}>
              Undo changes
            </Button>
          )}
          <Button type="button" size="sm" onClick={save} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
